import { useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useTemplatePermissions from '../../hooks/useTemplatePermissions';
import {
  getTemplateDeleteBlockedMessage,
  isTemplateArchived,
  isTemplateDeletable,
} from '../../utils/templateArchiveUtils';
import { isRejectedTemplate } from '../../utils/getRejectionReason';
import useTemplateSx from '../../hooks/useTemplateSx';

export default function TemplateRowActions({
  row,
  onEdit,
  onSend,
  onInsights,
  onDelete,
  onArchive,
  onRestore,
  onDuplicate,
  onSync,
  onCheckHealth,
  isSyncing = false,
}) {
  const styles = useTemplateSx();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const { canEdit, canDelete, canSend, canViewPerformance } = useTemplatePermissions();
  const isApproved = String(row?.status ?? '').toLowerCase() === 'approved';
  const isArchived = isTemplateArchived(row);
  const isRejected = isRejectedTemplate(row);
  const showInsights = Boolean(onInsights) && canViewPerformance;

  const showEdit = canEdit && !isArchived && isRejected;
  const showSend = canSend && isApproved && !isArchived;
  const showArchive = canEdit && !isArchived && Boolean(onArchive);
  const showRestore = canEdit && isArchived && Boolean(onRestore);
  const showDuplicate = canEdit && !isArchived && Boolean(onDuplicate);
  const showSync = canEdit && !isArchived && Boolean(onSync);
  const showHealth = Boolean(onCheckHealth);
  const showDelete = canDelete && isArchived && isTemplateDeletable(row, row.archive_delete_wait_days);
  const showDeleteDisabled = canDelete && isArchived && !isTemplateDeletable(row, row.archive_delete_wait_days);

  const handleClose = () => setAnchorEl(null);

  if (
    !showEdit &&
    !showSend &&
    !showInsights &&
    !showDelete &&
    !showDeleteDisabled &&
    !showArchive &&
    !showRestore &&
    !showDuplicate &&
    !showSync &&
    !showHealth
  ) {
    return null;
  }

  return (
    <Box sx={styles.actionColumnCell}>
      <IconButton
        size="small"
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-label="Actions"
        sx={styles.rowActionButton}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {showEdit ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onEdit?.(row);
            }}
          >
            Edit
          </MenuItem>
        ) : null}
        {showSync ? (
          <MenuItem
            disabled={isSyncing}
            onClick={() => {
              handleClose();
              onSync?.(row);
            }}
          >
            {isSyncing ? 'Syncing...' : 'Sync'}
          </MenuItem>
        ) : null}
        {showHealth ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onCheckHealth?.(row);
            }}
          >
            Check Health
          </MenuItem>
        ) : null}
        {showSend ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onSend?.(row);
            }}
          >
            Send
          </MenuItem>
        ) : null}
        {showInsights ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onInsights?.(row);
            }}
          >
            Performance
          </MenuItem>
        ) : null}
        {showDuplicate ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onDuplicate?.(row);
            }}
          >
            Duplicate
          </MenuItem>
        ) : null}
        {showArchive ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onArchive?.(row);
            }}
          >
            Archive
          </MenuItem>
        ) : null}
        {showRestore ? (
          <MenuItem
            onClick={() => {
              handleClose();
              onRestore?.(row);
            }}
          >
            Restore
          </MenuItem>
        ) : null}
        {showDeleteDisabled ? (
          <Tooltip title={getTemplateDeleteBlockedMessage(row, row.archive_delete_wait_days)} placement="left">
            <span>
              <MenuItem disabled sx={{ color: 'text.disabled' }}>
                Delete
              </MenuItem>
            </span>
          </Tooltip>
        ) : null}
        {showDelete ? (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              handleClose();
              onDelete?.(row);
            }}
          >
            Delete
          </MenuItem>
        ) : null}
      </Menu>
    </Box>
  );
}
